import { ZOOM_OPTIONS, FRAME_OPTIONS } from "../data/screenPolish";
import { Children, cloneElement, useEffect, useState } from "react";
import BaseField from "./Field";
import api from "../lib/api";

const TONES = ["professional","friendly","energetic","instructional","enthusiastic","reverent"];

const FIELD_LABELS = {
  language: "Language", voice: "Voice", voiceover_mode: "Voice-over", tone: "Tone",
  video_style: "Video style", aspect_ratio: "Aspect ratio", broll_mode: "B-roll",
  caption_style: "Captions", duration_target: "Duration",
  screen_zoom: "Auto-zoom", screen_frame: "Frame",
  bg_music_key: "Music", bg_music_volume: "Music volume",
  screen_audio_mode: "Recording audio", llm_provider_id: "LLM provider",
  tts_provider_id: "TTS provider", stt_provider_id: "STT provider",
};

const humanize = (k) => FIELD_LABELS[k] || k.replace(/_/g, " ");

// Provider.kind values (backend/models.py Provider) that have their own stock
// voice catalog. Anything else ("emergent", "openai_compatible", etc.) uses
// the OpenAI-shaped default catalog — same fallback `/providers/stock-voices`
// applies server-side for unrecognized `kind` query values.
const TTS_CATALOG_KINDS = ["sarvam", "elevenlabs", "edge", "pravaani", "muapi"];
const ttsCatalogKeyFor = (providerKind) =>
  TTS_CATALOG_KINDS.includes(providerKind) ? providerKind : "openai";

function Changed({ diff, field }) {
  if (!diff || !diff[field]) return null;
  return (
    <span className="text-2xs uppercase tracking-widest text-ink-accent ml-2"
          data-testid={`changed-${field}`}>
      changed
    </span>
  );
}

/* Delegates to the shared Field primitive so all 13 controls in this panel
   get a real htmlFor/id pair; `field` is used as the id so it stays stable
   and readable in the DOM. Keeps the `Changed` marker in the label. */
function Field({ label, field, diff, hint, children }) {
  return (
    <BaseField
      label={label}
      hint={hint}
      labelExtra={<Changed diff={diff} field={field} />}
    >
      {cloneElement(Children.only(children), { id: `setting-${field}` })}
    </BaseField>
  );
}

export default function SettingsPanel({ proj, saving, onChange }) {
  const isScreenRecording = proj.source_type === "screen_recording";
  const [langs, setLangs] = useState([]);
  const [ttsCatalog, setTtsCatalog] = useState({ openai: [], sarvam: [], elevenlabs: [], edge: [], pravaani: [], muapi: [] });
  const [music, setMusic] = useState([]);
  const [providers, setProviders] = useState([]);
  const diff = proj.settings_diff || {};
  const set = (k) => (e) => onChange({ [k]: e.target.value });

  useEffect(() => {
    api.get("/providers/languages").then((r) => setLangs(r.data?.languages || [])).catch(() => {});
    // Pre-fetch every stock-voice catalog kind up front (mirrors NewProject.jsx's
    // pattern) so the Voice select can be re-keyed instantly as the user changes
    // the TTS provider below, with each endpoint failure isolated via .catch.
    Promise.all(
      ["sarvam", "elevenlabs", "openai", "edge", "pravaani", "muapi"].map((k) =>
        api.get(`/providers/stock-voices?kind=${k}`).then((r) => r.data?.voices || []).catch(() => [])
      )
    ).then(([sarvam, elevenlabs, openai, edge, pravaani, muapi]) => {
      setTtsCatalog({ sarvam, elevenlabs, openai, edge, pravaani, muapi });
    });
    api.get("/music/library").then((r) => setMusic(r.data?.tracks || r.data || [])).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data || [])).catch(() => {});
  }, []);

  const byType = (t) => providers.filter((p) => p.type === t);
  // Voice ids mean different things per TTS provider kind (OpenAI name, Sarvam
  // speaker id, ElevenLabs voice_id, Edge id, Pravaani speaker) — show the
  // catalog matching whichever TTS provider is currently selected, falling
  // back to the same "openai" default the project-creation wizard uses.
  const selectedTtsProvider = providers.find((p) => p.provider_id === proj.tts_provider_id);
  const ttsCatalogKey = selectedTtsProvider ? ttsCatalogKeyFor(selectedTtsProvider.kind) : "openai";
  const voices = ttsCatalog[ttsCatalogKey] || [];

  return (
    <div className="brutal-card mt-4" data-testid="settings-panel">
      <h2 className="marquee-tag mb-3">// SETTINGS</h2>

      <h3 className="marquee-tag mb-2 mt-2">Voice &amp; Language</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Language" field="language" diff={diff}>
          <select className="brutal-select" value={proj.language || "en"} disabled={saving}
                  onChange={set("language")} data-testid="settings-language">
            {/* A stored language the picker doesn't list would otherwise leave
                the select blank; show it as-is so the user sees what the
                project is set to and can pick a supported one. */}
            {langs.length > 0 && proj.language && !langs.some((l) => l.code === proj.language) && (
              <option value={proj.language}>{proj.language} (not supported)</option>
            )}
            {langs.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </Field>
        <Field label="Voice" field="voice" diff={diff}>
          {/* A stored voice (e.g. the wizard's OpenAI default "nova") may not
              exist in whichever catalog the current TTS provider uses -- most
              visibly Pravaani, whose catalog is empty unless PRAVAANI_VOICES
              is set. Rather than render a <select> with no matching <option>
              (a blank box), fall back to the always-present "Default voice"
              so the control always shows something truthful; the backend
              treats voice=="" as "use this provider's own default speaker"
              on every TTS path. */}
          <select className="brutal-select"
                  value={voices.some((v) => v.id === proj.voice) ? proj.voice : ""}
                  disabled={saving} onChange={set("voice")} data-testid="settings-voice">
            <option value="">Default voice</option>
            {voices.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Voice-over" field="voiceover_mode" diff={diff}>
          <select className="brutal-select" value={proj.voiceover_mode || "tts"} disabled={saving}
                  onChange={set("voiceover_mode")} data-testid="settings-voiceover-mode">
            <option value="tts">AI narration</option>
            <option value="raw">My own recording</option>
          </select>
        </Field>
        <Field label="Tone" field="tone" diff={diff}>
          <select className="brutal-select" value={proj.tone || "professional"} disabled={saving}
                  onChange={set("tone")} data-testid="settings-tone">
            {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>

      <h3 className="marquee-tag mb-2 mt-4">Video</h3>
      <div className="grid md:grid-cols-2 gap-4">
        {/* Slideshow-only settings. The render pipeline has two branches and
            these are read by neither the screen-recording one nor anything it
            calls, so offering them there promises an effect that never
            arrives. */}
        {!isScreenRecording && (
          <Field label="Video style" field="video_style" diff={diff}>
            <select className="brutal-select" value={proj.video_style || "slideshow"} disabled={saving}
                    onChange={set("video_style")} data-testid="settings-video-style">
              <option value="slideshow">Slideshow — screenshots only</option>
              <option value="sora">AI intro clip + slideshow</option>
            </select>
          </Field>
        )}
        {!isScreenRecording && (
          <Field label="Slide motion" field="slide_motion" diff={diff}>
            <select className="brutal-select" value={proj.slide_motion || "none"} disabled={saving}
                    onChange={set("slide_motion")} data-testid="settings-slide-motion">
              <option value="none">Static — no movement</option>
              <option value="kenburns">Ken Burns — slow zoom (recommended)</option>
            </select>
          </Field>
        )}
        {isScreenRecording && (
          <Field label="Auto-zoom" field="screen_zoom" diff={diff}>
            <select className="brutal-select" value={proj.screen_zoom || "off"} disabled={saving}
                    onChange={set("screen_zoom")} data-testid="settings-screen-zoom">
              {ZOOM_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        )}
        {isScreenRecording && !(proj.screen_events || []).length && (proj.screen_zoom || "off") !== "off" && (
          <p className="text-xs text-ink-muted md:col-span-2 -mt-2" data-testid="zoom-needs-extension">
            This recording was made without the DemoCraft browser extension, so there are no clicks
            or pointer movements for auto-zoom to follow. Record with the extension to use it.
          </p>
        )}
        {isScreenRecording && (
          <Field label="Frame" field="screen_frame" diff={diff}>
            <select className="brutal-select" value={proj.screen_frame || "none"} disabled={saving}
                    onChange={set("screen_frame")} data-testid="settings-screen-frame">
              {FRAME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        )}
        <Field label="Aspect ratio" field="aspect_ratio" diff={diff}>
          <select className="brutal-select" value={proj.aspect_ratio || "16:9"} disabled={saving}
                  onChange={set("aspect_ratio")} data-testid="settings-aspect-ratio">
            <option value="16:9">16:9 — Landscape</option>
            <option value="9:16">9:16 — Vertical (Reels/Shorts/TikTok)</option>
            <option value="1:1">1:1 — Square</option>
          </select>
        </Field>
        {/* Both branches read this: slideshows weave clips between slides,
            recordings cut away to them while the screen is still. */}
        <Field label="Stock B-roll" field="broll_mode" diff={diff}
               hint={isScreenRecording
                 ? "Stock clips replace the picture during pauses; narration and captions keep running."
                 : undefined}>
          <select className="brutal-select" value={proj.broll_mode || "off"} disabled={saving}
                  onChange={set("broll_mode")} data-testid="settings-broll-mode">
            {isScreenRecording ? (
              <>
                <option value="off">Off</option>
                <option value="stock">On — stock cutaways over quiet stretches</option>
              </>
            ) : (
              <>
                <option value="off">Off — screenshots only</option>
                <option value="stock">On — free Pexels/Pixabay clips</option>
              </>
            )}
          </select>
        </Field>
        <Field label="Captions" field="caption_style" diff={diff}>
          <select className="brutal-select" value={proj.caption_style || "sidecar"} disabled={saving}
                  onChange={set("caption_style")} data-testid="settings-caption-style">
            <option value="off">Off</option>
            <option value="sidecar">Separate .srt file</option>
            <option value="burn">Burnt into the video</option>
          </select>
        </Field>
        {/* Only affects burnt-in captions; a sidecar .srt is styled by the
            player, not by us. */}
        {proj.caption_style === "burn" && (
          <>
            <Field label="Caption position" field="caption_position" diff={diff}>
              <select className="brutal-select" value={proj.caption_position || "bottom"} disabled={saving}
                      onChange={set("caption_position")} data-testid="settings-caption-position">
                <option value="bottom">Bottom</option>
                <option value="middle">Middle</option>
                <option value="top">Top</option>
              </select>
            </Field>
            <Field label="Caption size" field="caption_size" diff={diff}>
              <select className="brutal-select" value={proj.caption_size || "medium"} disabled={saving}
                      onChange={set("caption_size")} data-testid="settings-caption-size">
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large — best for phones</option>
              </select>
            </Field>
          </>
        )}
        {/* A recording's length is fixed by the recording, so a target is
            meaningless here — it is not even a prompt hint on that branch. */}
        {!isScreenRecording && (
        <Field label="Duration target (seconds)" field="duration_target" diff={diff}>
          <input type="number" min="15" max="600" className="brutal-input" disabled={saving}
                 value={proj.duration_target || 60} data-testid="settings-duration"
                 onChange={(e) => onChange({ duration_target: parseInt(e.target.value || "60", 10) })}/>
        </Field>
        )}
      </div>

      <h3 className="marquee-tag mb-2 mt-4">Audio</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Background music" field="bg_music_key" diff={diff}>
          <select className="brutal-select" value={proj.bg_music_key || ""} disabled={saving}
                  onChange={set("bg_music_key")} data-testid="settings-music">
            <option value="">None</option>
            {music.map((m) => <option key={m.key} value={m.key}>{m.name || m.key}</option>)}
          </select>
        </Field>
        <Field label="Music volume" field="bg_music_volume" diff={diff}>
          <input type="range" min="0" max="1" step="0.05" disabled={saving}
                 value={proj.bg_music_volume ?? 0.15} data-testid="settings-music-volume"
                 onChange={(e) => onChange({ bg_music_volume: parseFloat(e.target.value) })}/>
        </Field>
        {proj.source_type === "screen_recording" && (
          <Field label="Recording audio" field="screen_audio_mode" diff={diff}>
            <select className="brutal-select" value={proj.screen_audio_mode || "keep_recording"}
                    disabled={saving} onChange={set("screen_audio_mode")}
                    data-testid="settings-screen-audio-mode">
              <option value="keep_recording">Keep my recorded audio</option>
              <option value="ai_voice">Replace with AI narration</option>
            </select>
          </Field>
        )}
      </div>

      <h3 className="marquee-tag mb-2 mt-4">Providers</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {[["llm_provider_id", "LLM", "llm"], ["tts_provider_id", "TTS", "tts"], ["stt_provider_id", "STT", "stt"]].map(
          ([field, label, type]) => (
            <Field key={field} label={label} field={field} diff={diff}>
              <select className="brutal-select" value={proj[field] || ""} disabled={saving}
                      onChange={set(field)} data-testid={`settings-${type}-provider`}>
                <option value="">Default</option>
                {byType(type).map((p) => (
                  <option key={p.provider_id} value={p.provider_id}>{p.name}</option>
                ))}
              </select>
            </Field>
          )
        )}
      </div>

      <div className="text-xs text-ink-muted uppercase tracking-widest mt-4">
        Saved instantly — click <span className="font-bold">{proj.status === "failed" ? "Retry" : "Regenerate"}</span> to re-render.
      </div>
    </div>
  );
}

export { humanize, FIELD_LABELS };
